import Exif from 'exif-js';
import { type TFunction } from '@i18n/client';

interface FileValidationResult {
  file: File;
  isValid: boolean;
  reason?: string;
  reasonKey?: reasonType;
}

export enum reasonType {
  fileType = 'wrongFormat',
  tooSmall = 'tooSmall',
  tooBig = 'tooBig',
}

const SUPPORTED_FORMATS = 'jpeg, png, pdf';

const JPEG_SOI: number[][] = [[0xff, 0xd8]];

const MAGIC_NUMBERS: Record<string, number[][]> = {
  'image/jpeg': JPEG_SOI,
  'image/jpg': JPEG_SOI,
  'image/png': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  'image/webp': [[0x52, 0x49, 0x46, 0x46]],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
};

async function isFileOfType(
  file: File,
  t: TFunction
): Promise<FileValidationResult> {
  const unsupportedResult: FileValidationResult = {
    file,
    isValid: false,
    reason: t('common:file.unsupportedFileType', {
      formats: SUPPORTED_FORMATS,
    }),
    reasonKey: reasonType.fileType,
  };

  try {
    const allSignatures = Object.values(MAGIC_NUMBERS).flat();
    const maxLength = getLongestMagicNumberLength(allSignatures);
    const headerBytes = await readFileHeader(file, maxLength);

    for (const signature of allSignatures) {
      if (signature.every((byte, index) => byte === headerBytes[index])) {
        return { file, isValid: true };
      }
    }
  } catch (error) {
    console.error('Error reading file header:', error);
  }

  return unsupportedResult;
}

async function readFileHeader(file: File, length: number): Promise<number[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result !== null && reader.result instanceof ArrayBuffer) {
        resolve(Array.from(new Uint8Array(reader.result.slice(0, length))));
      } else {
        reject(new Error('Failed to read file header.'));
      }
    };

    reader.onerror = reject;

    reader.readAsArrayBuffer(file.slice(0, length));
  });
}

function getLongestMagicNumberLength(magicNumbersForType: number[][]): number {
  return magicNumbersForType.reduce(
    (maxLength, magicNumber) => Math.max(maxLength, magicNumber.length),
    0
  );
}

const getJpgResolution = (
  file: File
): Promise<{ x: number | undefined; y: number | undefined }> => {
  return new Promise((resolve) => {
    const xResolution = Exif.getTag(file, 'XResolution');
    const yResolution = Exif.getTag(file, 'YResolution');
    resolve({ x: xResolution, y: yResolution });
  });
};

const getPNGResolution = async (
  file: File
): Promise<{ x: number | undefined; y: number | undefined }> => {
  const bytes = await readFileHeader(file, 25);
  const width =
    (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
  const height =
    (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];
  return { x: width, y: height };
};

const validateFileSize = (
  file: File,
  t: TFunction,
  minSizeKB: number = 8,
  maxSizeMB: number = 10
): FileValidationResult => {
  if (file.size < minSizeKB * 1024) {
    return {
      file,
      isValid: false,
      reason: t('common:file.minLimit', { minSizeKB }),
      reasonKey: reasonType.tooSmall,
    };
  } else if (file.size > maxSizeMB * 1024 * 1024) {
    return {
      file,
      isValid: false,
      reason: t('common:file.maxLimit', { maxSizeMB }),
      reasonKey: reasonType.tooBig,
    };
  }
  return { file, isValid: true };
};

const validateResolution = async (
  file: File,
  t: TFunction
): Promise<FileValidationResult> => {
  const resolution = await (file.type === 'image/png'
    ? getPNGResolution(file)
    : file.type === 'image/jpeg'
      ? getJpgResolution(file)
      : Promise.resolve(undefined));
  if (
    resolution &&
    resolution.x &&
    resolution.y &&
    resolution.x < 96 &&
    resolution.y < 96
  ) {
    return {
      file,
      isValid: true,
      reason: t('common:file.insufficientResolution'),
    };
  }
  return { file, isValid: true };
};

export const validateFile = async (
  t: TFunction,
  file: File
): Promise<FileValidationResult> => {
  const sizeResult = validateFileSize(file, t);
  if (!sizeResult.isValid) return sizeResult;

  const typeResult = await isFileOfType(file, t);
  if (!typeResult.isValid) return typeResult;

  return await validateResolution(file, t);
};
